import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL } from '@core/config/api-url.token';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { Observable } from 'rxjs';
import {
  AudienceFilterRequestDto, AudiencePreviewDto, CampaignDto, CampaignKpiDto, CampaignRecipientDto,
  CreateCampaignRequestDto, EmailPreviewRequestDto, PagedCampaignsDto, ScheduleCampaignRequestDto,
  UpdateCampaignRequestDto,
} from '../../dtos/campaign.dto';

@Injectable()
export class CampaignsRemoteDatasource {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);

  public list(
    organizationId: number, page: number, pageSize: number, status?: string, searchTerm?: string,
  ): Observable<PagedCampaignsDto> {
    const params: Record<string, string | number> = { page, pageSize };
    if (status) params['status'] = status;
    if (searchTerm) params['searchTerm'] = searchTerm;

    return this.http.get<PagedCampaignsDto>(`${this.apiUrl}${API_ENDPOINTS.campaigns.list(organizationId)}`, { params });
  }

  public getKpis(organizationId: number): Observable<CampaignKpiDto> {
    return this.http.get<CampaignKpiDto>(`${this.apiUrl}${API_ENDPOINTS.campaigns.kpis(organizationId)}`);
  }

  public getById(organizationId: number, campaignId: string): Observable<CampaignDto> {
    return this.http.get<CampaignDto>(`${this.apiUrl}${API_ENDPOINTS.campaigns.byId(organizationId, campaignId)}`);
  }

  public getRecipients(organizationId: number, campaignId: string): Observable<CampaignRecipientDto[]> {
    return this.http.get<CampaignRecipientDto[]>(
      `${this.apiUrl}${API_ENDPOINTS.campaigns.recipients(organizationId, campaignId)}`,
    );
  }

  public previewAudience(organizationId: number, payload: AudienceFilterRequestDto): Observable<AudiencePreviewDto> {
    return this.http.post<AudiencePreviewDto>(
      `${this.apiUrl}${API_ENDPOINTS.campaigns.audiencePreview(organizationId)}`, payload,
    );
  }

  // Response body is the rendered email HTML as a raw string (the controller returns `Ok(htmlString)`),
  // not a JSON envelope -- `responseType: 'text'` is required or Angular's default JSON parsing throws.
  public previewEmail(organizationId: number, payload: EmailPreviewRequestDto): Observable<string> {
    return this.http.post(
      `${this.apiUrl}${API_ENDPOINTS.campaigns.previewEmail(organizationId)}`, payload, { responseType: 'text' },
    );
  }

  public create(organizationId: number, payload: CreateCampaignRequestDto): Observable<CampaignDto> {
    return this.http.post<CampaignDto>(`${this.apiUrl}${API_ENDPOINTS.campaigns.list(organizationId)}`, payload);
  }

  public update(organizationId: number, campaignId: string, payload: UpdateCampaignRequestDto): Observable<CampaignDto> {
    return this.http.put<CampaignDto>(
      `${this.apiUrl}${API_ENDPOINTS.campaigns.byId(organizationId, campaignId)}`, payload,
    );
  }

  public delete(organizationId: number, campaignId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}${API_ENDPOINTS.campaigns.byId(organizationId, campaignId)}`);
  }

  public duplicate(organizationId: number, campaignId: string): Observable<CampaignDto> {
    return this.http.post<CampaignDto>(
      `${this.apiUrl}${API_ENDPOINTS.campaigns.duplicate(organizationId, campaignId)}`, {},
    );
  }

  public pause(organizationId: number, campaignId: string): Observable<CampaignDto> {
    return this.http.post<CampaignDto>(
      `${this.apiUrl}${API_ENDPOINTS.campaigns.pause(organizationId, campaignId)}`, {},
    );
  }

  public schedule(organizationId: number, campaignId: string, payload: ScheduleCampaignRequestDto): Observable<CampaignDto> {
    return this.http.post<CampaignDto>(
      `${this.apiUrl}${API_ENDPOINTS.campaigns.schedule(organizationId, campaignId)}`, payload,
    );
  }

  public sendNow(organizationId: number, campaignId: string): Observable<CampaignDto> {
    return this.http.post<CampaignDto>(
      `${this.apiUrl}${API_ENDPOINTS.campaigns.sendNow(organizationId, campaignId)}`, {},
    );
  }
}
